// Shared by the create and edit user dialogs: the departments list, its main department and the sef flags
import * as api from "./api";
import { roleLabel, SEF_ROLES } from "@/config/roles";
const emptyRow = () => ({ department_id: null, is_sef: false });

export default {
  data() {
    return {
      departmentOptions: [],
      hiddenDepartmentIds: [],
      deptRows: [emptyRow()],
      mainDepartmentId: null,
      // a main department that is not in the departments table ("Profesor"): kept as it is
      legacyMainDepartment: null,
      departmentsError: "",
    };
  },
  computed: {
    selectedRoleName() {
      const role = (this.roles || []).find((r) => r.id === this.selectedRole);
      return role ? role.role : "";
    },
    roleAllowsSef() {
      return SEF_ROLES.includes(this.selectedRoleName);
    },
    roleItems() {
      return (this.roles || []).map((r) => ({ id: r.id, label: roleLabel(r.role), role: r.role }));
    },
  },
  watch: {
    // somebody becoming a department head heads their only department
    selectedRoleName(name, old) {
      if (!SEF_ROLES.includes(name) || SEF_ROLES.includes(old || "")) return;
      const filled = this.deptRows.filter((r) => r.department_id);
      if (filled.length === 1 && !filled[0].is_sef) filled[0].is_sef = true;
    },
    deptRows: {
      deep: true,
      handler() {
        if (this.deptRows.some((r) => r.department_id)) this.departmentsError = "";
      },
    },
  },
  methods: {
    async loadDepartmentOptions() {
      const all = await api.getDepartments();
      this.hiddenDepartmentIds = all.filter((d) => d.is_hidden).map((d) => d.id);
      // departments hidden in Configurare take no new members
      this.departmentOptions = all
        .filter((d) => d.parent_id === 1 && !d.is_hidden)
        .map((d) => ({ id: d.id, name: d.name }))
        .sort((a, b) => a.name.localeCompare(b.name, "ro"));
    },
    // from GET /users/:id -> departments: [{ id, name, isSef, isPrimary }]
    setDepartmentsFromUser(list, mainName) {
      const known = (list || []).filter((d) => d.id);
      // memberships can point at sub-units (003 falls back to them) or hidden departments, which the list lacks:
      // without an option the row shows blank and mainDepartmentName() finds no name
      const missing = known.filter((d) => !this.departmentOptions.some((o) => o.id === d.id));
      if (missing.length) {
        const hidden = this.hiddenDepartmentIds || [];
        this.departmentOptions = [
          ...this.departmentOptions,
          ...missing.map((d) => ({ id: d.id, name: d.name, hidden: hidden.includes(d.id) })),
        ];
      }
      this.deptRows = known.length ? known.map((d) => ({ department_id: d.id, is_sef: !!d.isSef })) : [emptyRow()];
      const main = known.find((d) => d.isPrimary);
      this.mainDepartmentId = main ? main.id : known[0] ? known[0].id : null;
      this.legacyMainDepartment = mainName && !known.some((d) => d.name === mainName) ? mainName : null;
    },
    resetDepartments() {
      this.deptRows = [emptyRow()];
      this.mainDepartmentId = null;
      this.legacyMainDepartment = null;
      this.departmentsError = "";
    },
    validateDepartments() {
      const filled = this.deptRows.filter((r) => r.department_id);
      if (!filled.length && !this.legacyMainDepartment) {
        this.departmentsError = "Alegeți cel puțin un departament";
        return false;
      }
      // a department head approves for at least one department: their only one, or the ones ticked
      if (this.roleAllowsSef && filled.length && !filled.some((r) => r.is_sef)) {
        if (filled.length > 1) {
          this.departmentsError = "Bifați «Șef» la departamentul pe care îl conduce";
          return false;
        }
        filled[0].is_sef = true;
      }
      this.departmentsError = "";
      return true;
    },
    departmentsPayload() {
      return this.deptRows
        .filter((r) => r.department_id)
        .map((r) => ({ department_id: r.department_id, is_sef: this.roleAllowsSef && !!r.is_sef }));
    },
    mainDepartmentName() {
      if (this.legacyMainDepartment) return this.legacyMainDepartment;
      const rows = this.deptRows.filter((r) => r.department_id);
      const id = rows.some((r) => r.department_id === this.mainDepartmentId) ? this.mainDepartmentId : rows[0] && rows[0].department_id;
      const found = this.departmentOptions.find((d) => d.id === id);
      return found ? found.name : null;
    },
  },
};
